import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDebouncedValue } from '@hetivo/hetivo-lib-front-utils';
import { INSPECTION_SEARCH_MAX_LENGTH } from '@hetivo/hetivo-lib-types';
import type {
  InspectionListItem,
  InspectionStats,
  InspectionStatsCriteria,
  InspectionTab,
} from '@hetivo/hetivo-lib-types';
import type { MultiSelectOption } from '@hetivo/hetivo-lib-web-utils';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { useMasterContext } from '&/master';

import type { SearchInspectionsParams } from '../api';
import type { InspectionsListSearch } from '../lib/inspections-list-search';
import { TAB_STATUS, toInspectionTab } from '../lib/inspections-list-search';
import { useInspectionLocationOptions } from './use-inspection-location-options';
import { useInspectionStats } from './use-inspection-stats';
import { useInspectionsSearch } from './use-inspections-search';

// ─── Types ────────────────────────────────────────────────────────────────────

/** The list's URL search state + a merge-setter, bound to the route by {@link useInspectionsListSearchState}. */
export interface InspectionsListSearchState {
  search: InspectionsListSearch;
  setSearch: (patch: Partial<InspectionsListSearch>) => void;
}

export interface UseInspectionsPageReturn {
  activeTab: InspectionTab;
  currentPage: number;
  /** A search or a location narrows the list: an empty tab reads the no-match message (SWOOP-401, FR-009). */
  hasFilter: boolean;
  /** The list request failed: the page shows the load-error alert instead of the table. */
  hasLoadError: boolean;
  inspections: InspectionListItem[];
  isLoading: boolean;
  /** The Location filter options (SWOOP-401, FR-005): inside the role scope and the master selector's choice. */
  locationOptions: MultiSelectOption[];
  /** The chosen location id; `undefined` is "All locations". */
  locationValue: string | undefined;
  onLocationChange: (locationId: string | undefined) => void;
  onPageChange: (page: number) => void;
  onRetry: () => void;
  onSearchChange: (value: string) => void;
  onTabChange: (tab: InspectionTab) => void;
  pageSize: number;
  /** The search box's text, as typed; the list follows it once typing settles. */
  searchValue: string;
  /** The tab counts; `undefined` while loading and whenever a request failed, so no tab reads a false 0. */
  stats: InspectionStats | undefined;
  totalItems: number;
  totalPages: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

/** How long typing must settle before the search reaches the URL and the list (the Issues debounce). */
const SEARCH_DEBOUNCE_MS = 300;

/** The longest search sent, the API's own limit (SWOOP-401): the box never holds more. */
export const MAX_SEARCH_LENGTH = INSPECTION_SEARCH_MAX_LENGTH;

// ─── Scope ────────────────────────────────────────────────────────────────────

/**
 * The master selector's narrowing, as the stats and search criteria: a key is sent only when one is selected,
 * never `null`. The page and the sidebar badge both build their criteria here, so they hash to one stats key.
 */
export function useInspectionScope(): InspectionStatsCriteria {
  const { siteId, territoryId } = useMasterContext();

  return useMemo(
    (): InspectionStatsCriteria => ({ locationId: siteId ?? undefined, territoryId: territoryId ?? undefined }),
    [siteId, territoryId],
  );
}

// ─── Route glue ───────────────────────────────────────────────────────────────

/** Binds the list state to the route's URL search params. Intra-list changes use `replace: true`. */
export function useInspectionsListSearchState(): InspectionsListSearchState {
  const search = useSearch({ from: '/_authenticated/inspections' });
  const navigate = useNavigate();
  const setSearch = useCallback(
    (patch: Partial<InspectionsListSearch>): void => {
      void navigate({
        replace: true,
        search: (prev: InspectionsListSearch): InspectionsListSearch => ({ ...prev, ...patch }),
        to: '/inspections',
      });
    },
    [navigate],
  );
  return { search, setSearch };
}

// ─── Page reset ───────────────────────────────────────────────────────────────

/**
 * The page to list: a new master selector scope resets it to 1. The page lives in the URL, so the reset is a
 * navigation; until it lands this returns 1 (not the stale page, which may not exist in the new scope, and
 * must not be fetched), and the scope is only marked settled once the URL page is 1.
 */
function useScopeResetPage(
  scope: InspectionStatsCriteria,
  urlPage: number,
  setSearch: InspectionsListSearchState['setSearch'],
): number {
  const [pageScope, setPageScope] = useState(scope);
  const scopeChanged = pageScope !== scope;
  useEffect(() => {
    if (!scopeChanged) {
      return;
    }
    if (urlPage === 1) {
      setPageScope(scope);
    } else {
      setSearch({ page: 1 });
    }
  }, [scope, scopeChanged, setSearch, urlPage]);
  return scopeChanged ? 1 : urlPage;
}

// ─── Search and location (SWOOP-401) ──────────────────────────────────────────

interface CommittedSearch {
  onSearchChange: (value: string) => void;
  searchValue: string;
}

/**
 * The search box state: what is typed is debounced, then committed to the URL with page 1 once it settles. The
 * second condition of the effect is the Issues race fix: a term typed later than the debounced one is never
 * overtaken by it, so the results always belong to the last text typed.
 */
function useCommittedSearch(
  committed: string | undefined,
  setSearch: InspectionsListSearchState['setSearch'],
): CommittedSearch {
  const [rawSearch, setRawSearch] = useState(() => committed ?? '');
  const committedSearch = committed ?? '';
  const debouncedSearch = useDebouncedValue(rawSearch, SEARCH_DEBOUNCE_MS).trim();

  useEffect(() => {
    if (debouncedSearch !== committedSearch && debouncedSearch === rawSearch.trim()) {
      setSearch({ page: 1, search: debouncedSearch || undefined });
    }
  }, [committedSearch, debouncedSearch, rawSearch, setSearch]);

  const onSearchChange = useCallback((value: string): void => {
    setRawSearch(value.slice(0, MAX_SEARCH_LENGTH));
  }, []);

  return { onSearchChange, searchValue: rawSearch };
}

interface LocationFilter {
  locationOptions: MultiSelectOption[];
  onLocationChange: (locationId: string | undefined) => void;
}

/**
 * The Location filter: choosing writes the location to the URL with page 1. Once the options are known, a location
 * that is not among them (a master selector change, a role change, an edited link) goes back to "All locations"
 * (FR-012). The list does not wait for the options: the API already answers nothing for a location out of scope.
 */
function useLocationFilter(
  location: string | undefined,
  setSearch: InspectionsListSearchState['setSearch'],
): LocationFilter {
  const { isLoaded, options } = useInspectionLocationOptions();

  useEffect(() => {
    if (isLoaded && location !== undefined && !options.some((option) => option.value === location)) {
      setSearch({ location: undefined, page: 1 });
    }
  }, [isLoaded, location, options, setSearch]);

  const onLocationChange = useCallback(
    (locationId: string | undefined): void => {
      setSearch({ location: locationId, page: 1 });
    },
    [setSearch],
  );

  return { locationOptions: options, onLocationChange };
}

/**
 * The page's criteria: the master selector's, with the chosen location in place of its `locationId` (research R1:
 * the options never reach outside the master selector's choice) and the committed search, at most 100 characters.
 * With neither set it equals the badge's criteria, so the two share one stats query.
 */
function usePageCriteria(scope: InspectionStatsCriteria, search: InspectionsListSearch): InspectionStatsCriteria {
  const term = search.search?.trim().slice(0, MAX_SEARCH_LENGTH) || undefined;
  return useMemo(
    (): InspectionStatsCriteria => ({ ...scope, locationId: search.location ?? scope.locationId, search: term }),
    [scope, search.location, term],
  );
}

/** The rows and paging of a list answer, with the empty defaults while it loads or after it failed. */
function toListResult(
  data: { items: InspectionListItem[]; total: number; totalPages: number } | undefined,
): Pick<UseInspectionsPageReturn, 'inspections' | 'totalItems' | 'totalPages'> {
  return { inspections: data?.items ?? [], totalItems: data?.total ?? 0, totalPages: data?.totalPages ?? 1 };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Derives the Inspections list view from the URL search state and the master selector (SWOOP-400): the active
 * tab (a status), the page, the rows and the three tab counts. A tab change or a master selector change resets
 * to page 1. SWOOP-401 adds the vehicle search and the Location filter: both narrow the rows and the three counts,
 * and changing either resets to page 1 and keeps the tab. With neither set, the counts share the badge's query.
 */
export function useInspectionsPage({ search, setSearch }: InspectionsListSearchState): UseInspectionsPageReturn {
  const scope = useInspectionScope();
  const activeTab = toInspectionTab(search.tab);
  const criteria = usePageCriteria(scope, search);
  const { onSearchChange, searchValue } = useCommittedSearch(search.search, setSearch);
  const { locationOptions, onLocationChange } = useLocationFilter(search.location, setSearch);

  const currentPage = useScopeResetPage(scope, search.page ?? 1, setSearch);

  const searchParams = useMemo(
    (): SearchInspectionsParams => ({
      criteria: { ...criteria, status: TAB_STATUS[activeTab] },
      limit: PAGE_SIZE,
      page: currentPage,
    }),
    [activeTab, criteria, currentPage],
  );

  const list = useInspectionsSearch(searchParams);
  // The page's own counts follow its search and location; the menu badge stays on the master scope (FR-013).
  const counts = useInspectionStats(criteria);

  const onTabChange = useCallback(
    (tab: InspectionTab): void => {
      setSearch({ page: 1, tab });
    },
    [setSearch],
  );
  const onPageChange = useCallback(
    (page: number): void => {
      setSearch({ page });
    },
    [setSearch],
  );
  const { refetch: refetchList } = list;
  const { isError: statsFailed, refetch: refetchStats } = counts;
  const onRetry = useCallback((): void => {
    void refetchList();
    if (statsFailed) {
      void refetchStats();
    }
  }, [refetchList, refetchStats, statsFailed]);

  return {
    activeTab,
    currentPage,
    hasFilter: criteria.search !== undefined || search.location !== undefined,
    hasLoadError: list.isError,
    isLoading: list.isLoading,
    locationOptions,
    locationValue: search.location,
    onLocationChange,
    onPageChange,
    onRetry,
    onSearchChange,
    onTabChange,
    pageSize: PAGE_SIZE,
    searchValue,
    stats: list.isError || statsFailed ? undefined : counts.data,
    ...toListResult(list.data),
  };
}
